/**
 * Catalog market-trend toggle — TCG catalog expansion Phase 4.
 *
 * Shows a 90-day market-price chart for ANY catalog product (owned or not),
 * expanded inline inside a lookup/browse grid tile. Used by:
 *   - quickCardLookup (Pokemon API grid + catalog grid for all other games)
 *   - sealedProductPicker (sealed catalog browse)
 *
 * Chart.js + the chart module load lazily on first use (same bundle-lean
 * dynamic-import pattern as lotDrawer / lotHistory).
 *
 * Note: card snapshots (catalog_prices) only exist since the 2026-07-04
 * catalog go-live — charts thicken daily. Sealed snapshots go back further.
 */
import { icon } from "../utils/icon";

const HOST_CLASS = "catalog-trend-host";

/** Small toggle button for grid tiles. Pass the data-* attribute that the
 *  caller's delegated click handler keys on (e.g. `data-qcl-trend="3"`). */
export function trendButtonHtml(dataAttr: string): string {
  return `<button class="btn btn-ghost !py-0.5 !px-1.5 !text-[.65rem] mt-1" ${dataAttr} title="90-day market trend">${icon("trending-up", { size: 11 })} Trend</button>`;
}

/** True when the click landed inside an expanded trend chart — callers use
 *  this to keep chart interactions from triggering the tile's pick action. */
export function isTrendClick(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && !!target.closest(`.${HOST_CLASS}`);
}

/** Toggle the inline 90-day trend chart inside a grid tile. */
export async function toggleCatalogTrend(
  tile: HTMLElement,
  kind: "card" | "sealed",
  productId: number,
): Promise<void> {
  const existing = tile.querySelector(`.${HOST_CLASS}`);
  if (existing) { existing.remove(); return; }

  const host = document.createElement("div");
  host.className = `${HOST_CLASS} mt-2 pt-2 border-t border-[var(--border)] w-full`;
  host.innerHTML = `<div class="text-xs text-[var(--muted)] py-2">Loading trend\u2026</div>`;
  tile.appendChild(host);

  try {
    const mod = await import("./priceHistoryChart");
    // Bail if the user closed the tile while the module loaded.
    if (!host.isConnected) return;
    const wrap = document.createElement("div");
    wrap.style.height = "150px";
    const canvas = document.createElement("canvas");
    wrap.appendChild(canvas);
    host.innerHTML = "";
    host.appendChild(wrap);
    const chart = kind === "sealed"
      ? await mod.renderSealedPriceHistory(canvas, productId, 90)
      : await mod.renderCatalogPriceHistory(canvas, productId, 90);
    if (!chart) {
      host.innerHTML = `<div class="text-xs text-[var(--muted)] py-2">No price history yet \u2014 daily snapshots are still accumulating for this product.</div>`;
    }
  } catch (e) {
    console.warn("[catalogTrend] failed:", e);
    host.innerHTML = `<div class="text-xs text-rose-300 py-2">Couldn\u2019t load the trend chart.</div>`;
  }
}
